import * as React from 'react'
import * as Types from './types'
import { TopicPreferencesLayoutAttributes } from './constants'
import { getConfiguration } from '../utils'
import { isTruthy } from 'generic/utility'

const MockTopicCard = ({ title, isActive, cardSize, borderColor, activeBorderColor }: any) => {
  const cardStyle = {
    transition: 'all 0.3s ease',
    borderRadius: '8px',
    border: `1px solid ${isActive ? activeBorderColor : borderColor}`,
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
    height: cardSize === 'small' ? '60px' : '80px',
    backgroundColor: isActive ? '#f8f9fa' : '#ffffff',
    padding: cardSize === 'small' ? '12px' : '16px',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    minWidth: cardSize === 'small' ? '140px' : '180px',
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0px',
    gap: '12px',
  }

  const titleStyle = {
    margin: 0,
    fontWeight: 500,
    color: '#343a40',
    fontSize: cardSize === 'small' ? '0.9rem' : '1.1rem',
    flex: 1,
    minWidth: 0, // Allows text to wrap/truncate if needed
  }

  const toggleStyle = {
    width: cardSize === 'small' ? '32px' : '40px',
    height: cardSize === 'small' ? '16px' : '20px',
    borderRadius: '20px',
    backgroundColor: isActive ? '#7FAE1B' : '#ccc',
    position: 'relative' as const,
    cursor: 'default',
    flexShrink: 0,
  }

  const toggleKnobStyle = {
    width: cardSize === 'small' ? '12px' : '16px',
    height: cardSize === 'small' ? '12px' : '16px',
    borderRadius: '50%',
    backgroundColor: 'white',
    position: 'absolute' as const,
    top: '2px',
    left: isActive ? (cardSize === 'small' ? '18px' : '22px') : '2px',
    transition: 'left 0.3s ease',
  }

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <h6 style={titleStyle}>{title}</h6>
        <div style={toggleStyle}>
          <div style={toggleKnobStyle}></div>
        </div>
      </div>
    </div>
  )
}

const HeaderComponent = ({ configurations }: any) => {
  const pageTitle = getConfiguration(configurations, TopicPreferencesLayoutAttributes.PageTitle, 'Topic Preferences')
  const headerDescription = getConfiguration(configurations, TopicPreferencesLayoutAttributes.HeaderDescription, 'Personalize your newsletter experience by selecting topics that matter to you.')
  const showHeader = getConfiguration(configurations, TopicPreferencesLayoutAttributes.ShowHeader, true)
  const headerImageUrl = getConfiguration(configurations, TopicPreferencesLayoutAttributes.HeaderImageUrl) ||
                        getConfiguration(configurations, TopicPreferencesLayoutAttributes.BrandHeaderImageUrl)

  return (
    <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
      {showHeader && headerImageUrl && (
        <img 
          src={headerImageUrl} 
          alt="Header" 
          style={{ maxHeight: '80px', marginBottom: '1rem' }} 
        />
      )}
      <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#2c3e50', marginBottom: '1rem' }}>
        {pageTitle}
      </h1>
      <div 
        style={{ 
          color: '#6c757d', 
          fontSize: '1.2rem', 
          maxWidth: '600px', 
          margin: '0 auto' 
        }}
        dangerouslySetInnerHTML={{ __html: headerDescription }}
      />
    </div>
  )
}

export const PreviewComponent = ({ configurations, data, communityTopics = [] }: Types.WrappedState & { communityTopics?: any[] }) => {
  const cardSize = getConfiguration(data, TopicPreferencesLayoutAttributes.TopicCardSize, 'large')
  const borderColor = getConfiguration(data, TopicPreferencesLayoutAttributes.TopicBorderColor, '#dee2e6')
  const activeBorderColor = getConfiguration(data, TopicPreferencesLayoutAttributes.ActiveTopicBorderColor, '#7FAE1B')
  const saveButtonText = getConfiguration(data, TopicPreferencesLayoutAttributes.SaveButtonText, 'Save Preferences')
  const saveButtonBgColor = getConfiguration(data, TopicPreferencesLayoutAttributes.SaveButtonBackgroundColor, '#7FAE1B')
  const saveButtonTextColor = getConfiguration(data, TopicPreferencesLayoutAttributes.SaveButtonTextColor, '#ffffff')
  const enableCustomTopics = isTruthy(getConfiguration(data, TopicPreferencesLayoutAttributes.EnableCustomTopics, true))
  const hideRecommendedTopics = isTruthy(getConfiguration(data, TopicPreferencesLayoutAttributes.HideRecommendedTopics, false))

  const mockTopics = [
    { title: 'Technology', isActive: true },
    { title: 'Sports', isActive: false },
    { title: 'Health', isActive: true },
    { title: 'Business', isActive: false },
    { title: 'Entertainment', isActive: true },
    { title: 'Science', isActive: false },
  ]

  // Convert communityTopics to the format expected by the preview
  const adminTopics = communityTopics.map(topic => ({
    title: topic.name || topic.topic,
    isActive: false // Admin topics are shown as suggestions, not active by default
  }))

  const mockCustomTopics = [
    { title: 'AI & Machine Learning', isActive: true },
    { title: 'Cryptocurrency', isActive: false },
  ]

  return (
    <div style={{ padding: '2rem', backgroundColor: '#ffffff' }}>
      <HeaderComponent configurations={data} />
      
      {/* How it works section */}
      <div style={{ 
        marginBottom: '1.5rem', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px', 
        border: '1px solid #dee2e6' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1.2rem' }}>ℹ️</span>
          <span style={{ fontWeight: 500 }}>How it works:</span>
        </div>
        <p style={{ margin: 0 }}>Toggle the switch next to each topic to customize your content. When a topic is active, you'll receive related content in your newsletter.</p>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem', color: '#343a40' }}>Your Current Topics</h3>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '8px',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          borderLeft: `4px solid ${activeBorderColor}`
        }}>
          {mockTopics.filter(topic => topic.isActive).map((topic, index) => (
            <MockTopicCard
              key={index}
              title={topic.title}
              isActive={topic.isActive}
              cardSize={cardSize}
              borderColor={borderColor}
              activeBorderColor={activeBorderColor}
            />
          ))}
        </div>
      </div>

      {/* Show Recommended Topics section if: not hidden OR (hidden but has predefined topics) */}
      {(!hideRecommendedTopics || (hideRecommendedTopics && adminTopics.length > 0)) && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#343a40' }}>Recommended Topics</h3>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            borderLeft: '4px solid #ffc107'
          }}>
            {(() => {
              // If hideRecommendedTopics is enabled, only show predefined (admin) topics
              if (hideRecommendedTopics) {
                return adminTopics.map((topic, index) => (
                  <MockTopicCard
                    key={index}
                    title={topic.title}
                    isActive={topic.isActive}
                    cardSize={cardSize}
                    borderColor={borderColor}
                    activeBorderColor={activeBorderColor}
                  />
                ));
              }

              // Otherwise, combine admin topics with mock topics to show at least 5 total
              const mockSuggestedTopics = mockTopics.filter(topic => !topic.isActive);
              let topicsToShow = [...adminTopics];

              // If we have fewer than 5 topics, add mock topics to reach at least 5
              if (topicsToShow.length < 5) {
                const needed = 5 - topicsToShow.length;
                const additionalTopics = mockSuggestedTopics.slice(0, needed);
                topicsToShow = [...topicsToShow, ...additionalTopics];
              }

              return topicsToShow.map((topic, index) => (
                <MockTopicCard
                  key={index}
                  title={topic.title}
                  isActive={topic.isActive}
                  cardSize={cardSize}
                  borderColor={borderColor}
                  activeBorderColor={activeBorderColor}
                />
              ));
            })()}
          </div>
        </div>
      )}

      {/* Add Custom Topics Section - Only show if enabled */}
      {enableCustomTopics && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#343a40' }}>Add Custom Topics</h3>
          <div style={{ 
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            borderLeft: '4px solid #17a2b8'
          }}>
            {/* Show custom topics if any */}
            {mockCustomTopics.length > 0 && (
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px',
                marginBottom: '1.5rem'
              }}>
                {mockCustomTopics.map((topic, index) => (
                  <MockTopicCard
                    key={index}
                    title={topic.title}
                    isActive={topic.isActive}
                    cardSize={cardSize}
                    borderColor={borderColor}
                    activeBorderColor={activeBorderColor}
                  />
                ))}
              </div>
            )}
            
            {/* Input for adding custom topics */}
            <div style={{
              padding: '1.5rem',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <label style={{ fontWeight: 500, marginBottom: '0.5rem', color: '#495057', display: 'block' }}>Enter Topics</label>
              <input
                type="text"
                placeholder="Enter topics separated by commas (e.g., Technology, Sports, Health)"
                disabled
                style={{ 
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  marginBottom: '0.5rem',
                  backgroundColor: '#f8f9fa',
                  cursor: 'not-allowed'
                }}
              />
              <small style={{ color: '#6c757d' }}>
                <strong>Press Enter</strong> to add topics. They will appear above this input field.
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <button
          disabled
          style={{
            backgroundColor: saveButtonBgColor,
            color: saveButtonTextColor,
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '4px',
            fontSize: '16px',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            cursor: 'not-allowed',
            opacity: 0.8,
            transition: 'all 0.2s ease'
          }}
        >
          {saveButtonText}
        </button>
      </div>
    </div>
  )
}
